/**
 * Create Volume page component.
 *
 * @module pages/CreateVolumePage
 */

import { useState, useRef } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Cloud, HardDrive, ChevronDown } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Input,
  LabelsEditor,
} from '@/components/ui';
import type { LabelsEditorRef } from '@/components/ui';
import { useCreateVolume, useCreateSandboxGroupVolume } from '@/hooks';
import type { VolumeResponse } from '@/types';

type VolumeKind = 'AzureBlob' | 'DataDisk';

interface FormState {
  name: string;
  volumeKind: VolumeKind;
  size: string; // only for DataDisk, e.g. "1Gi"
  labels: Record<string, string>;
}

const initialFormState: FormState = {
  name: '',
  volumeKind: 'AzureBlob',
  size: '1Gi',
  labels: {},
};

/**
 * Create volume page
 */
export function CreateVolumePage() {
  const { groupId } = useParams<{ groupId?: string }>();
  const navigate = useNavigate();
  const createMutation = useCreateVolume();
  const createGroupMutation = useCreateSandboxGroupVolume();
  const basePath = groupId ? `/sandbox-groups/${encodeURIComponent(groupId)}/volumes` : '/volumes';
  const [form, setForm] = useState<FormState>(initialFormState);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [showResults, setShowResults] = useState(false);
  const [additionalDetailsExpanded, setAdditionalDetailsExpanded] = useState(false);
  const [createdVolume, setCreatedVolume] = useState<VolumeResponse | null>(null);
  const [timeTakenMs, setTimeTakenMs] = useState<number>(0);

  // Ref for labels editor to get uncommitted draft
  const labelsEditorRef = useRef<LabelsEditorRef>(null);

  const handleChange = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormState, string>> = {};

    if (!form.name.trim()) {
      newErrors.name = 'Volume name is required.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Include any uncommitted draft label from the labels editor
    const finalLabels = { ...form.labels };
    const draft = labelsEditorRef.current?.getDraft();
    if (draft) {
      finalLabels[draft.key] = draft.value;
    }

    const startTime = performance.now();

    const mutationArgs = {
      name: form.name.trim(),
      type: form.volumeKind === 'DataDisk' ? 'DataDisk' : 'AzureBlob',
      size: form.volumeKind === 'DataDisk' ? form.size : undefined,
      labels: Object.keys(finalLabels).length > 0 ? finalLabels : undefined,
    };

    const onSuccess = (data: VolumeResponse) => {
      const endTime = performance.now();
      setTimeTakenMs(Math.round(endTime - startTime));
      setCreatedVolume(data);
      setShowResults(true);
    };

    if (groupId) {
      createGroupMutation.mutate({ groupId, ...mutationArgs }, { onSuccess });
    } else {
      createMutation.mutate(mutationArgs, { onSuccess });
    }
  };

  const handleViewVolumes = () => {
    navigate(basePath);
  };

  const handleCreateAnother = () => {
    setShowResults(false);
    setCreatedVolume(null);
    setTimeTakenMs(0);
    setForm(initialFormState);
  };

  // Results page after successful creation
  if (showResults && createdVolume) {
    return (
      <div>
        <PageHeader
          title="Volume Created"
          description="Your volume has been created successfully."
          breadcrumbs={[
            { label: 'Dashboard', href: '/dashboard' },
            { label: 'Volumes', href: basePath },
            { label: 'Create' },
          ]}
        />

        <div className="space-y-6">
          <Card>
            <CardHeader title="Creation Summary" description="Volume creation details." />
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="text-sm text-green-700 dark:text-green-400">Status</div>
                  <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                    Success
                  </div>
                </div>
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="text-sm text-blue-700 dark:text-blue-400">Time Taken</div>
                  <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                    {timeTakenMs.toLocaleString()} ms
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Volume Details" description="Details of the created volume." />
            <CardBody>
              <div className="space-y-3">
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Name</div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {createdVolume.volumeName}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Type</div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {createdVolume.type === 'DataDisk' ? 'Data Disk' : createdVolume.type}
                  </div>
                </div>
                {createdVolume.size && (
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Size</div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {createdVolume.size}
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Labels</div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {createdVolume.labels && Object.keys(createdVolume.labels).length > 0 ? (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {Object.entries(createdVolume.labels).map(([key, value]) => (
                          <span
                            key={key}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                          >
                            {key}: {String(value)}
                          </span>
                        ))}
                      </div>
                    ) : (
                      'N/A'
                    )}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardFooter>
              <div className="flex justify-end gap-3">
                <Button variant="secondary" onClick={handleCreateAnother}>
                  Create Another
                </Button>
                <Button variant="secondary" onClick={handleViewVolumes}>
                  View All Volumes
                </Button>
                <Button
                  onClick={() =>
                    navigate(`${basePath}/${encodeURIComponent(createdVolume.volumeName)}`)
                  }
                >
                  View Volume
                </Button>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  // Normal form view
  return (
    <div>
      <PageHeader
        title="Create Volume"
        description="Create a new persistent storage volume."
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Volumes', href: basePath },
          { label: 'Create' },
        ]}
        action={
          <Link to={basePath}>
            <Button variant="secondary" leftIcon={<ArrowLeft className="h-4 w-4" />}>
              Cancel
            </Button>
          </Link>
        }
      />

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader title="Basic Information" />
            <CardBody className="space-y-4">
              <Input
                label="Name"
                placeholder="my-volume"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                error={errors.name}
                helperText="A unique name for your volume (e.g., project-data, shared-assets)."
                required
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Type
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, volumeKind: 'AzureBlob' }))}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm border rounded-lg transition-colors cursor-pointer ${
                      form.volumeKind === 'AzureBlob'
                        ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-400 dark:border-primary-600 text-primary-700 dark:text-primary-300 font-medium'
                        : 'bg-white dark:bg-[#161616] border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <Cloud className="h-4 w-4" />
                    Azure Blob
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, volumeKind: 'DataDisk' }))}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm border rounded-lg transition-colors cursor-pointer ${
                      form.volumeKind === 'DataDisk'
                        ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-400 dark:border-primary-600 text-primary-700 dark:text-primary-300 font-medium'
                        : 'bg-white dark:bg-[#161616] border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <HardDrive className="h-4 w-4" />
                    Data Disk
                  </button>
                </div>
                <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                  {form.volumeKind === 'DataDisk'
                    ? 'Persistent POSIX-compliant block device volume. Can only be mounted in one sandbox at a time.'
                    : 'Persistent volume backed by Azure Blob Storage.'}
                </p>
              </div>
              {form.volumeKind === 'DataDisk' && (
                <Input
                  label="Size"
                  placeholder="1Gi"
                  value={form.size}
                  onChange={(e) => handleChange('size', e.target.value)}
                  helperText="Size of the data disk (e.g. 64Mi, 256Mi, 1Gi, 10Gi). Minimum: 64Mi, Maximum: 10Gi."
                  required
                />
              )}
            </CardBody>
            <CardFooter>
              <div className="flex justify-end gap-3">
                <Link to={basePath}>
                  <Button variant="secondary" type="button">
                    Cancel
                  </Button>
                </Link>
                <Button
                  type="submit"
                  leftIcon={<Save className="h-4 w-4" />}
                  isLoading={groupId ? createGroupMutation.isPending : createMutation.isPending}
                >
                  Create Volume
                </Button>
              </div>
            </CardFooter>
          </Card>

          {/* Additional Details - Collapsible */}
          <Card>
            <button
              type="button"
              onClick={() => setAdditionalDetailsExpanded(!additionalDetailsExpanded)}
              className={`w-full px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors ${additionalDetailsExpanded ? 'border-b border-gray-100 dark:border-[#2a2a2a]' : ''}`}
            >
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-left">
                  Additional Details
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 text-left">
                  Configure labels for organization and filtering.
                </p>
              </div>
              <ChevronDown
                className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${additionalDetailsExpanded ? 'rotate-180' : ''}`}
              />
            </button>
            <div
              className={`grid transition-[grid-template-rows] duration-200 ease-out ${additionalDetailsExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
            >
              <div className="overflow-hidden">
                <CardBody>
                  <div className="space-y-4">
                    <LabelsEditor
                      ref={labelsEditorRef}
                      value={form.labels}
                      onChange={(labels) => setForm((prev) => ({ ...prev, labels }))}
                      helperText="Add key-value pairs to label this volume for organization and filtering."
                    />
                  </div>
                </CardBody>
              </div>
            </div>
          </Card>
        </div>
      </form>
    </div>
  );
}
